import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent, type Ref } from "react";
import { cadStatusText, type CadGate } from "../desk/cad.ts";
import { consentRemainingLabel } from "../desk/consentClock.ts";
import { createPasteChord, type LocalPaste } from "../desk/localClipboard.ts";
import type { PortalEnvironment } from "../config.ts";
import { WyloWatermark } from "./chrome.tsx";
import { sessionWindowTitle } from "../sessionTitle.ts";
import { SessionQualityControls, closeQualityPopout, watchQualitySignOut, type SessionQualityView } from "./quality.tsx";
import type { Screen } from "../desk/model.ts";
import { primeVideo } from "../desk/playback.ts";
import { paintedFrame, pointerOnDisplay, tileFrame } from "./layout.ts";

function subscribeFullscreen(onChange: () => void): () => void {
  document.addEventListener("fullscreenchange", onChange);
  return () => document.removeEventListener("fullscreenchange", onChange);
}

function readFullscreenElement(): Element | null {
  return document.fullscreenElement;
}

function fullscreenPlaced(panel: HTMLElement | null, fullscreenElement: Element | null, local: boolean): boolean {
  return (panel !== null && fullscreenElement === panel) || local;
}

function useConsentNow(active: boolean): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [active]);
  return nowMs;
}

function statusCopy(screen: Screen, nowMs: number): string | null {
  switch (screen.kind) {
    case "creating":
      return "Starting the session";
    case "waiting": {
      const base = `Waiting for consent on the device. Mode ${operatorModeLabel(screen.accessMode)}.`;
      if (screen.accessMode !== "Attended") return base;
      const remaining = consentRemainingLabel(nowMs, screen.deadlineAt, screen.requestedAt);
      return remaining ? `${base} ${remaining}` : base;
    }
    case "denied":
      return "Consent denied";
    case "timed-out":
      return "Consent timed out";
    case "connecting":
      return "Connecting";
    case "ended":
      return `Session ended. ${screen.reason}`;
    case "failed":
      return screen.message;
    default:
      return null;
  }
}

function assignVideo(ref: Ref<HTMLVideoElement>, node: HTMLVideoElement | null) {
  if (typeof ref === "function") ref(node);
  else if (ref) ref.current = node;
  if (node) primeVideo(node);
}

function isPaste(event: { code: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean {
  return event.code === "KeyV" && (event.ctrlKey || event.metaKey) && !event.altKey;
}

export function operatorModeLabel(mode: "Attended" | "Unattended"): string {
  return mode === "Attended" ? "Remote Control" : "Stealth Control";
}

export function sessionHeading(mode: "Attended" | "Unattended" | null): string {
  if (mode === "Attended") return "Remote Control session";
  if (mode === "Unattended") return "Stealth Control session";
  return "Session";
}

function headingMode(
  accessMode: "Attended" | "Unattended" | null | undefined,
  screen: Screen,
): "Attended" | "Unattended" | null {
  if (accessMode === "Attended" || accessMode === "Unattended") return accessMode;
  if (screen.kind === "waiting" || screen.kind === "live") return screen.accessMode;
  return null;
}

export function SessionScreen(props: {
  screen: Screen;
  accessMode?: "Attended" | "Unattended" | null;
  videoRef: Ref<HTMLVideoElement>;
  onSelectDisplay: (displayId: string) => void;
  onSetView: (view: "mosaic" | "single") => void;
  onEnd: () => void;
  onRemotePaste: (payload: LocalPaste) => void;
  onGesture?: () => void;
  onPointer: (input: { x: number; y: number; buttons: number; action: "move" | "down" | "up" }) => void;
  onKey: (input: { action: "down" | "up"; code: string; modifiers: Array<"Control" | "Alt" | "Shift" | "Meta"> }) => void;
  onWheel: (input: { deltaX: number; deltaY: number }) => void;
  cadGate: CadGate;
  secureAttention?: boolean;
  cadNotice: string | null;
  linkNotice?: string | null;
  clipboardNotice?: string | null;
  quality?: SessionQualityView | null;
  qualityArrivals?: readonly number[];
  environment?: PortalEnvironment;
  deviceId?: string;
  deviceName?: string;
  onSecureAttention: () => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoSink = useRef(props.videoRef);
  videoSink.current = props.videoRef;
  const onVideo = useCallback((node: HTMLVideoElement | null) => {
    assignVideo(videoSink.current, node);
    setVideoEl((prev) => (prev === node ? prev : node));
  }, []);
  const [mediaPx, setMediaPx] = useState<{ width: number; height: number } | null>(null);
  const pasteSink = useRef(props.onRemotePaste);
  pasteSink.current = props.onRemotePaste;
  const pasteField = useRef<HTMLTextAreaElement>(null);
  const chordRef = useRef<ReturnType<typeof createPasteChord> | null>(null);
  if (chordRef.current === null) {
    chordRef.current = createPasteChord(
      (payload, line) => {
        console.info(line);
        pasteSink.current(payload);
      },
      (line) => {
        console.info(line);
      },
    );
  }
  const chord = chordRef.current;
  const pointerDrag = useRef(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const [localFullscreen, setLocalFullscreen] = useState(false);
  const [barOpen, setBarOpen] = useState(false);
  const fullscreenElement = useSyncExternalStore(subscribeFullscreen, readFullscreenElement, () => null);
  const [stagePx, setStagePx] = useState({ width: 0, height: 0 });
  const screen = props.screen;
  const title = sessionHeading(headingMode(props.accessMode, screen));
  const stageWatch = screen.kind === "live" ? `${screen.view}:${screen.selectedDisplayId}` : screen.kind;
  const selectedId = screen.kind === "live" ? screen.selectedDisplayId : "";
  useEffect(() => {
    setMediaPx(null);
  }, [selectedId]);
  useEffect(() => {
    if (!videoEl) return;
    const read = () => {
      const width = videoEl.videoWidth;
      const height = videoEl.videoHeight;
      if (width < 1 || height < 1) return;
      setMediaPx((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
    };
    read();
    videoEl.addEventListener("loadedmetadata", read);
    videoEl.addEventListener("resize", read);
    return () => {
      videoEl.removeEventListener("loadedmetadata", read);
      videoEl.removeEventListener("resize", read);
    };
  }, [videoEl, selectedId]);
  useEffect(() => {
    if (props.deviceName === undefined) return;
    document.title = sessionWindowTitle(props.deviceName, "remote");
  }, [props.deviceName]);
  useEffect(() => {
    return () => closeQualityPopout(props.deviceId);
  }, [props.deviceId]);
  useEffect(() => {
    return watchQualitySignOut(() => {
      closeQualityPopout();
      props.onEnd();
    });
  }, [props.onEnd]);
  const sessionOverKind =
    screen.kind === "denied" || screen.kind === "timed-out" || screen.kind === "ended" || screen.kind === "failed"
      ? screen.kind
      : null;
  useEffect(() => {
    if (!sessionOverKind) return;
    closeQualityPopout(props.deviceId);
  }, [sessionOverKind, props.deviceId]);
  function finishSession() {
    closeQualityPopout(props.deviceId);
    props.onEnd();
  }
  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const read = () => {
      const rect = node.getBoundingClientRect();
      setStagePx((prev) => {
        if (prev.width === rect.width && prev.height === rect.height) return prev;
        return { width: rect.width, height: rect.height };
      });
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [stageWatch]);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const frame = frameRef.current;
      if (!fullscreenPlaced(frame, document.fullscreenElement, localFullscreen)) return;
      event.preventDefault();
      event.stopPropagation();
      if (frame !== null && document.fullscreenElement === frame && typeof document.exitFullscreen === "function") {
        void document.exitFullscreen();
      }
      setLocalFullscreen(false);
      setBarOpen(false);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [localFullscreen]);
  const consentClock = props.screen.kind === "waiting" && props.screen.accessMode === "Attended";
  const nowMs = useConsentNow(consentClock);
  const message = statusCopy(props.screen, nowMs);
  if (props.screen.kind !== "live") {
    return (
      <section className="panel">
        <h2>{title}</h2>
        {message ? <p role="status">{message}</p> : <p>Choose a device to start a session.</p>}
        {props.screen.kind === "waiting" || props.screen.kind === "connecting" ? (
          <button type="button" onClick={finishSession}>
            Cancel
          </button>
        ) : null}
        <WyloWatermark environment={props.environment} />
      </section>
    );
  }
  const live = props.screen;
  const selected = live.displays.find((display) => display.displayId === live.selectedDisplayId) ?? null;
  const frame = selected ? paintedFrame(live.view, stagePx, selected, live.displays, mediaPx) : null;
  const shown = live.view === "single" ? live.displays.filter((display) => display.displayId === live.selectedDisplayId) : live.displays;
  const placed = fullscreenPlaced(frameRef.current, fullscreenElement, localFullscreen);
  const cadStatus = cadStatusText(props.cadGate, props.cadNotice);
  const dockClass = placed ? (barOpen ? "view-switch fs-bar open" : "view-switch fs-bar shut") : "view-switch";
  return (
    <section className="panel session-live">
      <header className="panel-head">
        <h2>{title}</h2>
        <p>
          {live.accessMode}. One monitor is live. All monitors is the layout advertised by the device.
        </p>
      </header>
      <div className={placed && localFullscreen ? "remote-frame local-fs" : "remote-frame"} ref={frameRef}>
        {placed ? <div className="fs-hot" onMouseEnter={() => setBarOpen(true)} /> : null}
        <div
          className={dockClass}
          onMouseDown={(event) => event.preventDefault()}
          onMouseEnter={() => setBarOpen(true)}
          onMouseLeave={() => {
            if (placed) setBarOpen(false);
          }}
        >
          <button type="button" aria-pressed={live.view === "mosaic"} onClick={() => props.onSetView("mosaic")}>
            All monitors
          </button>
          <button type="button" aria-pressed={live.view === "single"} onClick={() => props.onSetView("single")}>
            Single monitor
          </button>
          <button type="button" onClick={finishSession}>
            End session
          </button>
          {props.secureAttention === false ? null : (
            <button
              type="button"
              disabled={props.cadGate !== "ready"}
              aria-busy={props.cadGate === "sending"}
              onClick={() => props.onSecureAttention()}
            >
              Ctrl+Alt+Del
            </button>
          )}
          <button type="button" onClick={toggleFullscreen}>
            {placed ? "Exit full screen" : "Full screen"}
          </button>
          <SessionQualityControls
            quality={props.quality ?? null}
            deviceId={props.deviceId}
            deviceName={props.deviceName}
            arrivals={props.qualityArrivals}
          />
        </div>
        {cadStatus ? (
          <p className="cad-toast" role="status" aria-live="polite">
            {cadStatus}
          </p>
        ) : null}
        {props.linkNotice ? (
          <p className="ice-toast" role="status">
            {props.linkNotice}
          </p>
        ) : null}
        {props.clipboardNotice ? (
          <p className="clip-toast" role="status">
            {props.clipboardNotice}
          </p>
        ) : null}
        <div
          ref={stageRef}
          className={live.view === "mosaic" ? "stage mosaic" : "stage single"}
          onMouseDown={(event) => {
            const target = event.target;
            if (target instanceof Element && target.closest("a")) return;
            event.preventDefault();
          }}
          onPointerDown={(event) => {
            props.onGesture?.();
            sendPointer(event, "down");
          }}
          onPointerMove={(event) => {
            nudgeBar(event.clientY);
            sendPointer(event, "move");
          }}
          onPointerUp={(event) => sendPointer(event, "up")}
          onPointerCancel={(event) => sendPointer(event, "up")}
          onContextMenu={(event) => event.preventDefault()}
          onPaste={(event) => {
            event.preventDefault();
            notePaste("stage", event.clipboardData);
            if (event.clipboardData) chord.onPaste(event.clipboardData);
          }}
          onKeyDownCapture={(event) => sendKey(event, "down")}
          onKeyUpCapture={(event) => sendKey(event, "up")}
          onWheel={(event) => props.onWheel({ deltaX: event.deltaX, deltaY: event.deltaY })}
          tabIndex={0}
        >
          <textarea
            ref={pasteField}
            className="paste-sink"
            tabIndex={-1}
            autoComplete="off"
            aria-label="Remote paste"
            onPaste={(event) => {
              event.preventDefault();
              event.stopPropagation();
              notePaste("paste-sink", event.clipboardData);
              if (event.clipboardData) chord.onPaste(event.clipboardData);
            }}
          />
          {shown.map((display) => (
            <button
              key={display.displayId}
              type="button"
              className={display.displayId === live.selectedDisplayId ? "tile selected" : "tile"}
              style={live.view === "mosaic" ? tileFrame(display, live.displays) : undefined}
              aria-pressed={display.displayId === live.selectedDisplayId}
              onClick={() => props.onSelectDisplay(display.displayId)}
            >
              <span>
                {display.name}
                {display.primary ? " · primary" : ""}
              </span>
              {display.displayId === live.selectedDisplayId ? null : <span className="tile-note">Not the live monitor</span>}
            </button>
          ))}
          {selected ? (
            <video
              ref={onVideo}
              style={
                frame
                  ? { left: `${frame.x}px`, top: `${frame.y}px`, width: `${frame.width}px`, height: `${frame.height}px` }
                  : { left: "0px", top: "0px", width: "100%", height: "100%" }
              }
              autoPlay
              playsInline
              muted
            />
          ) : null}
          <WyloWatermark anchored environment={props.environment} />
        </div>
      </div>
    </section>
  );

  function nudgeBar(clientY: number) {
    if (!fullscreenPlaced(frameRef.current, document.fullscreenElement, localFullscreen)) return;
    const frameBox = frameRef.current?.getBoundingClientRect();
    if (!frameBox) return;
    if (clientY - frameBox.top <= 12) setBarOpen(true);
  }

  function toggleFullscreen() {
    const frameNode = frameRef.current;
    if (!frameNode) return;
    if (fullscreenPlaced(frameNode, document.fullscreenElement, localFullscreen)) {
      if (document.fullscreenElement === frameNode && typeof document.exitFullscreen === "function") void document.exitFullscreen();
      setLocalFullscreen(false);
      setBarOpen(false);
      return;
    }
    setBarOpen(false);
    if (typeof frameNode.requestFullscreen !== "function") {
      setLocalFullscreen(true);
      return;
    }
    void frameNode.requestFullscreen();
  }

  function focusPasteEditor(reason: string) {
    pasteField.current?.focus();
    console.info(`clipboard hop step=focus target=paste-sink reason=${reason}`);
  }

  function notePaste(target: string, data: DataTransfer | null) {
    const text = data?.getData("text/plain") ?? "";
    const files = data?.files.length ?? 0;
    console.info(`clipboard hop step=paste target=${target} textBytes=${new TextEncoder().encode(text).length} files=${files}`);
  }

  function sendPointer(event: PointerEvent<HTMLDivElement>, action: "move" | "down" | "up") {
    const display = live.displays.find((item) => item.displayId === live.selectedDisplayId);
    if (!display) return;
    const stage = event.currentTarget;
    if (action === "down") focusPasteEditor("pointer");
    const bounds = stage.getBoundingClientRect();
    const local = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    const geometry = [live.view, { width: bounds.width, height: bounds.height }, display, live.displays, local, mediaPx] as const;
    const inside = pointerOnDisplay(...geometry);
    let armed = pointerDrag.current;
    if (action === "down") {
      if (!inside) return;
      armed = true;
      pointerDrag.current = true;
      stage.setPointerCapture?.(event.pointerId);
    }
    if (action === "up") {
      if (stage.hasPointerCapture?.(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      pointerDrag.current = false;
      if (!armed && !inside) return;
    }
    const mapped = inside ?? (armed ? pointerOnDisplay(...geometry, true) : null);
    if (!mapped) return;
    const buttons = action === "down" && event.buttons === 0 ? 1 : event.buttons;
    props.onPointer({ x: mapped.x, y: mapped.y, buttons, action });
  }

  function sendKey(event: ReactKeyboardEvent<HTMLDivElement>, action: "down" | "up") {
    if (action === "down" && !isPaste(event)) props.onGesture?.();
    const target = event.target;
    const onPasteField = target instanceof Node && target === pasteField.current;
    if (!onPasteField && target instanceof HTMLElement && target.closest("a.watermark, textarea, input, select")) return;
    if (event.code === "MetaLeft" || event.code === "MetaRight") {
      event.stopPropagation();
      if (action === "down") {
        chord.onMetaDown(event.code);
        focusPasteEditor("meta");
      } else {
        const outcome = chord.onMetaUp();
        if (outcome === "tap") {
          props.onKey({ action: "down", code: event.code, modifiers: [] });
          props.onKey({ action: "up", code: event.code, modifiers: [] });
        } else if (outcome === "up") {
          props.onKey({ action: "up", code: event.code, modifiers: [] });
        }
      }
      return;
    }
    if (isPaste(event)) {
      event.stopPropagation();
      if (action === "down") {
        console.info("clipboard hop step=keydown chord=paste");
        chord.onPasteKey();
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (action === "down") {
        if (document.activeElement === pasteField.current) pasteField.current?.blur();
        event.currentTarget.blur();
      }
      return;
    }
    if (action === "down") {
      const prefix = chord.onOtherKey();
      if (prefix) props.onKey({ action: "down", code: prefix, modifiers: [] });
    }
    event.preventDefault();
    event.stopPropagation();
    const modifiers: Array<"Control" | "Alt" | "Shift" | "Meta"> = [];
    if (event.ctrlKey) modifiers.push("Control");
    if (event.altKey) modifiers.push("Alt");
    if (event.shiftKey) modifiers.push("Shift");
    if (event.metaKey) modifiers.push("Meta");
    props.onKey({ action, code: event.code, modifiers });
  }
}
